import { debounce } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useMemo, useState, useEffect } from 'react';

import { Stack, Typography } from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { isShadowEmail } from 'src/services/candidates/candidates.utils';
import { useGetAccountsQuery } from 'src/services/organisation/organisation.service';
import { useGetOrganizationContactsQuery } from 'src/services/contacts/contacts.service';
import { useGetOrganizationCandidatesQuery } from 'src/services/candidates/candidates.service';
import { ScheduleMeetingAttendee, ScheduleMeetingAttendeeType } from 'src/services/calendar/calendar.types';

import { RHFSwitch, RHFAutocomplete } from 'src/components/hook-form';
import FeatureLockedChip from 'src/components/billing/feature-locked-chip';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { FeatureType } from 'src/types/subscription.types';

// ----------------------------------------------------------------------

export interface MeetingAttendeeOption {
  type: ScheduleMeetingAttendeeType;
  id: string;
  label: string;
  email?: string;
}

export type MeetingOptionsValues = {
  attendees: MeetingAttendeeOption[];
  online_meeting: boolean;
  add_bot: boolean;
  attending: boolean;
};

export const toScheduleMeetingAttendees = (attendees: MeetingAttendeeOption[]): ScheduleMeetingAttendee[] =>
  attendees.map(({ type, id }) => ({ type, id }));

const SEARCH_PAGE = { page: 1, per_page: 10 };

// MUI's groupBy only renders one header per group when the options are already sorted by group.
const GROUP_ORDER: ScheduleMeetingAttendeeType[] = ['user', 'contact', 'candidate'];

const isSameAttendee = (a: MeetingAttendeeOption, b: MeetingAttendeeOption) => a.type === b.type && a.id === b.id;

interface Props {
  disabled?: boolean;
}

export default function MeetingOptionsFields({ disabled }: Props) {

  const { t } = useTranslate();

  const authContext = useAuthContext();

  const { canAccess } = useFeatures();

  const { watch, setValue } = useFormContext<MeetingOptionsValues>();

  const [searchQuery, setSearchQuery] = useState<string>('');

  const { currentData: accounts, isFetching: isAccountsFetching } = useGetAccountsQuery({
    org_id: authContext.organization.id,
    page: SEARCH_PAGE,
    search_query: searchQuery,
  }, {
    skip: !authContext.organization.id,
  });

  const { currentData: contacts, isFetching: isContactsFetching } = useGetOrganizationContactsQuery({
    org_id: authContext.organization.id,
    page: SEARCH_PAGE,
    search_query: searchQuery,
  }, {
    skip: !authContext.organization.id,
  });

  const { currentData: candidates, isFetching: isCandidatesFetching } = useGetOrganizationCandidatesQuery({
    page: SEARCH_PAGE,
    search_query: searchQuery,
  });

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setSearchQuery(value);
    }, 500),
    [setSearchQuery]
  );

  useEffect(() => () => onSearchQueryChange.cancel(), [onSearchQueryChange]);

  const attendees = watch('attendees');
  const onlineMeeting = watch('online_meeting');
  const addBot = watch('add_bot');

  const canAddBot = canAccess(FeatureType.MEETINGS_ENABLED);

  useEffect(() => {
    if (addBot && (!onlineMeeting || !canAddBot)) {
      setValue('add_bot', false);
    }
  }, [addBot, onlineMeeting, canAddBot, setValue]);

  const options = useMemo(() => {
    const currentAccountId = authContext.account.id;

    // The current user joins through the "I'm attending" switch, not as an invitee.
    const teamOptions: MeetingAttendeeOption[] = (accounts?.results ?? [])
      .filter((account) => account.id !== currentAccountId)
      .map((account) => ({
        type: 'user',
        id: account.id as string,
        label: `${account.first_name} ${account.last_name}`,
        email: account.email || undefined,
      }));

    const contactOptions: MeetingAttendeeOption[] = (contacts?.results ?? []).map((contact) => ({
      type: 'contact',
      id: contact.id,
      label: `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim(),
      email: isShadowEmail(contact.email) ? undefined : contact.email,
    }));

    const candidateOptions: MeetingAttendeeOption[] = (candidates?.results ?? []).map((candidate) => {
      const email = candidate.candidate_profile?.email || candidate.email;
      return {
        type: 'candidate',
        id: candidate.id,
        label: `${candidate.candidate_profile?.first_name ?? ''} ${candidate.candidate_profile?.last_name ?? ''}`.trim(),
        email: isShadowEmail(email) ? undefined : email,
      };
    });

    const fetched = [...teamOptions, ...contactOptions, ...candidateOptions];

    // Keep selected people (including one pre-filled by the caller) in the list after a search stops
    // returning them, so their chips still render and MUI doesn't report the value as unmatched.
    const selectedMissing = (attendees ?? []).filter((attendee) => !fetched.some((option) => isSameAttendee(option, attendee)));

    return [...selectedMissing, ...fetched].sort(
      (a, b) => GROUP_ORDER.indexOf(a.type) - GROUP_ORDER.indexOf(b.type)
    );
  }, [accounts, contacts, candidates, attendees, authContext.account.id]);

  return (
    <Stack spacing={2}>
      <RHFAutocomplete
        name="attendees"
        multiple
        disabled={disabled}
        label={t('calendar.meeting_options.people')}
        placeholder={t('calendar.meeting_options.people_placeholder')}
        options={options}
        loading={isAccountsFetching || isContactsFetching || isCandidatesFetching}
        filterOptions={(x) => x}
        filterSelectedOptions
        groupBy={(option) => t(`calendar.meeting_options.groups.${option.type}`)}
        getOptionDisabled={(option) => !option.email}
        onInputChange={(_e, value) => onSearchQueryChange(value)}
        getOptionLabel={(option) => (option as MeetingAttendeeOption).label}
        isOptionEqualToValue={(option, value) => isSameAttendee(option, value)}
        getOptionKey={(option) => `${(option as MeetingAttendeeOption).type}-${(option as MeetingAttendeeOption).id}`}
        renderOption={({ key, ...rest }, option) => (
          <li key={key} {...rest}>
            <Stack direction="column">
              <Typography variant="body2">
                {option.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.email ?? t('calendar.meeting_options.no_email')}
              </Typography>
            </Stack>
          </li>
        )}
      />

      <Stack direction="row" alignItems="center" justifyContent="space-between" px={1}>
        <Stack direction="column" alignItems="start">
          <Typography variant="subtitle2">
            {t('calendar.meeting_options.online_meeting')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {t('calendar.meeting_options.online_meeting_helper')}
          </Typography>
        </Stack>
        <RHFSwitch name="online_meeting" disabled={disabled} />
      </Stack>

      {
        onlineMeeting === true && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" px={1}>
            <Stack direction="column" alignItems="start">
              <Stack direction="row" alignItems="center" justifyContent="start" spacing={1}>
                <Typography variant="subtitle2">
                  {t('calendar.meeting_options.add_bot')}
                </Typography>
                {
                  !canAddBot && (
                    <FeatureLockedChip text={t('common.upgrade_required')} variant='outlined' />
                  )
                }
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {t('calendar.meeting_options.add_bot_helper')}
              </Typography>
            </Stack>
            <RHFSwitch name="add_bot" disabled={disabled || !canAddBot} />
          </Stack>
        )
      }

      <Stack direction="row" alignItems="center" justifyContent="space-between" px={1}>
        <Stack direction="column" alignItems="start">
          <Typography variant="subtitle2">
            {t('calendar.meeting_options.attending')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {t('calendar.meeting_options.attending_helper')}
          </Typography>
        </Stack>
        <RHFSwitch name="attending" disabled={disabled} />
      </Stack>
    </Stack>
  );
}
